/**
 * 👤 CUSTOMER SELECTOR COMPONENT
 * Reusable component for searching and selecting customers in booking dialogs
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Stack,
  Button,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { Search, Person, Email, Phone, CheckCircle, Warning, VerifiedUser } from '@mui/icons-material';
import UserService, { User } from '@/services/userService';

interface CustomerSelectorProps {
  selectedCustomer: User | null;
  onSelectCustomer: (customer: User) => void;
  onCreateNew?: () => void; // Optional callback to create new customer
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  role?: 'customer' | 'all'; // Filter by role (default: 'customer')
}

export const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  selectedCustomer,
  onSelectCustomer,
  onCreateNew,
  label = 'Select Customer',
  placeholder = 'Search by name, email, or phone...',
  helperText,
  required = true,
  disabled = false,
  role = 'customer',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Search for customers
  useEffect(() => {
    const searchCustomers = async () => {
      if (searchTerm.trim().length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await UserService.getUsers({
          search: searchTerm.trim(),
          ...(role !== 'all' && { role }), // Only include role filter if not 'all'
          limit: 10,
        });

        // Debug: Log the actual response structure
        if (response.users && response.users.length > 0) {
        }

        setSearchResults(response.users || []);
        setShowResults(true);
      } catch (err) {
        console.error('Error searching customers:', err);
        setError('Failed to search customers. Please try again.');
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(searchCustomers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleSelectCustomer = (customer: User) => {
    onSelectCustomer(customer);
    setSearchTerm('');
    setShowResults(false);
    setSearchResults([]);
  };

  const handleClearSelection = () => {
    onSelectCustomer(null as any);
    setSearchTerm('');
    setSearchResults([]);
  };

  const formatPhoneNumber = (phone: string) => {
    // Basic formatting - can be enhanced
    return phone;
  };

  const getEliteTierColor = (tier?: string) => {
    switch (tier?.toLowerCase()) {
      case 'platinum':
        return 'primary';
      case 'gold':
        return 'warning';
      case 'silver':
        return 'default';
      default:
        return 'default';
    }
  };

  // Helper to get customer name from various possible structures
  const getCustomerName = (customer: User) => {
    // Try nested structure first
    if (customer.personalInfo?.firstName || customer.personalInfo?.lastName) {
      const name = `${customer.personalInfo.firstName || ''} ${customer.personalInfo.lastName || ''}`.trim();
      if (name) return name;
    }

    // Try flat structure
    const flatCustomer = customer as any;
    if (flatCustomer.firstName || flatCustomer.lastName) {
      const name = `${flatCustomer.firstName || ''} ${flatCustomer.lastName || ''}`.trim();
      if (name) return name;
    }

    // Fallback to name field (but not if it's "N/A")
    if (flatCustomer.name && flatCustomer.name !== 'N/A') {
      return flatCustomer.name;
    }

    // Last resort - use email or phone as identifier
    const email = getCustomerEmail(customer);
    const phone = getCustomerPhone(customer);
    if (email && email !== 'N/A') return email;
    if (phone && phone !== 'N/A') return phone;

    return 'Unknown Customer';
  };

  // Helper to get customer email from various possible structures
  const getCustomerEmail = (customer: User) => {
    const email = customer.personalInfo?.email || (customer as any).email;
    return email && email !== 'N/A' ? email : null;
  };

  // Helper to get customer phone from various possible structures
  const getCustomerPhone = (customer: User) => {
    const phone = customer.personalInfo?.phone || (customer as any).phone;
    return phone && phone !== 'N/A' ? phone : null;
  };

  // Helper to check email verification
  const isEmailVerified = (customer: User) => {
    return customer.accountInfo?.emailVerified || (customer as any).emailVerified || false;
  };

  // Helper to check if customer is fully verified for rentals
  const isCustomerVerified = (customer: User) => {
    const flatCustomer = customer as any;

    // Backend /api/v1/admin/users returns isVerified boolean field
    // This is the authoritative source for customer verification status
    return flatCustomer.isVerified === true;
  };

  // Helper to get verification badge color
  const getVerificationStatus = (customer: User) => {
    return isCustomerVerified(customer) ? 'verified' : 'unverified';
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Selected Customer Display */}
      {selectedCustomer && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            bgcolor: isCustomerVerified(selectedCustomer) ? 'success.50' : 'warning.50',
            border: isCustomerVerified(selectedCustomer) ? '1px solid' : '2px solid',
            borderColor: isCustomerVerified(selectedCustomer) ? 'success.200' : 'warning.main'
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: isCustomerVerified(selectedCustomer) ? 'success.main' : 'warning.main' }}>
              {selectedCustomer.personalInfo?.profilePicture || (selectedCustomer as any).profilePicture ? (
                <img src={selectedCustomer.personalInfo?.profilePicture || (selectedCustomer as any).profilePicture} alt={getCustomerName(selectedCustomer)} />
              ) : (
                <Person />
              )}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                <Typography variant="body1" fontWeight={600}>
                  {getCustomerName(selectedCustomer)}
                </Typography>
                {isCustomerVerified(selectedCustomer) ? (
                  <Chip
                    icon={<VerifiedUser />}
                    label="VERIFIED"
                    size="small"
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    icon={<Warning />}
                    label="UNVERIFIED"
                    size="small"
                    color="warning"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {(selectedCustomer as any).eliteTier && (
                  <Chip
                    label={(selectedCustomer as any).eliteTier}
                    size="small"
                    color={getEliteTierColor((selectedCustomer as any).eliteTier)}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={2}>
                {getCustomerEmail(selectedCustomer) && (
                  <Typography variant="caption" color="text.secondary">
                    <Email sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    {getCustomerEmail(selectedCustomer)}
                  </Typography>
                )}
                {getCustomerPhone(selectedCustomer) && (
                  <Typography variant="caption" color="text.secondary">
                    <Phone sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    {formatPhoneNumber(getCustomerPhone(selectedCustomer))}
                  </Typography>
                )}
              </Stack>
              {selectedCustomer.activitySummary && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {selectedCustomer.activitySummary.totalBookings || 0} bookings •
                  ₦{selectedCustomer.activitySummary.totalSpent?.toLocaleString() || 0} total spent
                </Typography>
              )}
            </Box>
            <Button size="small" onClick={handleClearSelection} disabled={disabled}>
              Change
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Search Input (only show when no customer selected) */}
      {!selectedCustomer && (
        <TextField
          fullWidth
          label={label}
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (e.target.value.trim().length >= 2) {
              setShowResults(true);
            }
          }}
          onFocus={(e) => {
            setAnchorEl(e.currentTarget);
            if (searchTerm.trim().length >= 2) {
              setShowResults(true);
            }
          }}
          disabled={disabled}
          required={required}
          helperText={helperText}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: loading && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Results */}
      <Popper
        open={showResults && !selectedCustomer && !!anchorEl}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorEl?.clientWidth }}
      >
        <ClickAwayListener onClickAway={() => setShowResults(false)}>
          <Paper
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              mt: 1,
            }}
            elevation={8}
          >
          {searchResults.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No customers found matching "{searchTerm}"
              </Typography>
              {onCreateNew && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    setShowResults(false);
                    setSearchTerm('');
                    onCreateNew();
                  }}
                >
                  Create New Customer
                </Button>
              )}
            </Box>
          ) : (
            <List>
              {searchResults.map((customer) => (
                <ListItem
                  key={customer.id}
                  disablePadding
                  sx={{
                    bgcolor: isCustomerVerified(customer) ? 'transparent' : 'warning.50',
                    borderLeft: isCustomerVerified(customer) ? 'none' : '3px solid',
                    borderColor: 'warning.main'
                  }}
                >
                  <ListItemButton onClick={() => handleSelectCustomer(customer)}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: isCustomerVerified(customer) ? 'default' : 'warning.main' }}>
                        {customer.personalInfo?.profilePicture || (customer as any).profilePicture ? (
                          <img src={customer.personalInfo?.profilePicture || (customer as any).profilePicture} alt={getCustomerName(customer)} />
                        ) : (
                          <Person />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" fontWeight={500}>
                            {getCustomerName(customer)}
                          </Typography>
                          {isCustomerVerified(customer) ? (
                            <Chip
                              icon={<VerifiedUser />}
                              label="VERIFIED"
                              size="small"
                              color="success"
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          ) : (
                            <Chip
                              icon={<Warning />}
                              label="UNVERIFIED"
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          )}
                          {(customer as any).eliteTier && (
                            <Chip
                              label={(customer as any).eliteTier}
                              size="small"
                              color={getEliteTierColor((customer as any).eliteTier)}
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <>
                          {getCustomerEmail(customer) && (
                            <Typography variant="caption" display="block">
                              {getCustomerEmail(customer)}
                            </Typography>
                          )}
                          {getCustomerPhone(customer) && (
                            <Typography variant="caption" display="block">
                              {formatPhoneNumber(getCustomerPhone(customer))}
                            </Typography>
                          )}
                          {customer.activitySummary && (
                            <Typography variant="caption" color="text.secondary">
                              {customer.activitySummary.totalBookings || 0} bookings
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Create New Customer Button */}
      {!selectedCustomer && !showResults && onCreateNew && (
        <Button
          variant="text"
          size="small"
          onClick={onCreateNew}
          disabled={disabled}
          sx={{ mt: 1 }}
        >
          + Create New Customer
        </Button>
      )}
    </Box>
  );
};

export default CustomerSelector;
